// 分割録画パートの並び順と事前チェック（移行フェーズ4・ブラウザ側）。
//
// DJI Osmo Action 5 Pro は約4GB（4Kで約10〜13分）ごとに録画を自動分割し、
// カメラ設定で無効化できない。1回の調理が複数ファイルになるため、
// まとめて選んで1本の録画として扱う。
//
// この順序が結合順序の正になる。間違えると調理工程の順番が壊れるので、
// 画面で「1本目 / 2本目」を示して人が直せるようにする。
// 厳密なコーデック判定はしない（ワーカー側の ffprobe が最終確認をする）。

/** DJI_<14桁>_<4桁>_D.MP4 */
const DJI_NAME = /^DJI_(\d{14})_(\d{4})_D\.(MP4|MOV)$/i;

export function parseDjiName(name: string): { timestamp: string; index: number } | null {
  const m = DJI_NAME.exec(name);
  return m ? { timestamp: m[1], index: Number(m[2]) } : null;
}

/**
 * 結合順に並べる。元配列は破壊しない。
 * DJI命名は (timestamp, index) 昇順、それ以外は自然順。混在時はDJI群を先に置く
 * （どちらが正解とも定まらないため、順序が安定することを優先する）。
 */
export function sortVideoFiles<T extends { name: string }>(files: T[]): T[] {
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
  return files.slice().sort((a, b) => {
    const da = parseDjiName(a.name);
    const db = parseDjiName(b.name);
    if (da && db) {
      if (da.timestamp !== db.timestamp) return da.timestamp < db.timestamp ? -1 : 1;
      if (da.index !== db.index) return da.index - db.index;
      return collator.compare(a.name, b.name);
    }
    if (da) return -1;
    if (db) return 1;
    return collator.compare(a.name, b.name);
  });
}

/** 結合の前提が崩れていそうな組み合わせを検出する（警告のみ。止めはしない） */
export function inspectVideoFiles(files: { name: string }[]): string[] {
  const warnings: string[] = [];
  if (files.length < 2) return warnings;

  const exts = new Set(files.map((f) => (f.name.includes(".") ? f.name.split(".").pop()!.toLowerCase() : "")));
  if (exts.size > 1) {
    warnings.push(`拡張子が混在しています（${[...exts].join(" / ")}）。同じ設定で撮影した動画を選んでください。`);
  }

  const djis = files.map((f) => parseDjiName(f.name)).filter((x): x is NonNullable<typeof x> => x !== null);
  if (djis.length > 1) {
    if (new Set(djis.map((d) => d.timestamp)).size > 1) {
      warnings.push("撮影開始時刻の異なるファイルが混ざっています。別々の録画を1本にしようとしていないか確認してください。");
    }
    const idx = djis.map((d) => d.index).sort((a, b) => a - b);
    for (let i = 1; i < idx.length; i++) {
      if (idx[i] !== idx[i - 1] + 1) {
        warnings.push(
          `連番が飛んでいます（${String(idx[i - 1]).padStart(4, "0")} の次が ${String(idx[i]).padStart(4, "0")}）。抜けているファイルがないか確認してください。`,
        );
        break;
      }
    }
  }
  return warnings;
}
